import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
// jl-design-system
import Link, { PrimaryLink, SecondaryLink } from 'jl-design-system/elements/link/Link';
import { PrimaryButton } from 'jl-design-system/elements/button/Button';
import Modal from 'jl-design-system/components/modal/Modal';
import Form from 'jl-design-system/components/form/Form';
import Message from 'jl-design-system/components/message/Message';
// redux
import { connect } from 'react-redux';
import { checkAgeRestrictionDate } from '../../redux/actions/user/userActions';
import { isApps } from '../../redux/reducers/app/appReducer';
import { removeItemsFromBasket } from '../../redux/actions/edit-basket/editBasketActions';
// config
import ageCheckFormConfig from '../../utils/form/configs/ageCheck';
import thumbnail from '../../utils/image/thumbnail';
import appConstants from '../../constants/appConstants';
//
import MyAccountLink from '../my-account-link/MyAccountLink';
import styles from './age-check-modal.scss';


export class AgeCheckModal extends PureComponent {

  static propTypes = {
    ageCheckError: PropTypes.shape({
      body: PropTypes.string,
      message: PropTypes.string,
    }),
    ageCheckMinimumAge: PropTypes.string,
    ageRestrictedProducts: PropTypes.arrayOf(PropTypes.object),
    ageRestrictedProductsCanRemove: PropTypes.bool,
    basketUrl: PropTypes.string,
    checkAgeRestrictionDate: PropTypes.func,
    hasSavedDob: PropTypes.bool,
    isApps: PropTypes.bool,
    isSignedIn: PropTypes.bool,
    rebatchOrderApiCallActive: PropTypes.bool,
    removeBasketItemsApiCallActive: PropTypes.bool,
    removeBasketItemsFailed: PropTypes.bool,
    removeItemsFromBasket: PropTypes.func,
    showModal: PropTypes.bool,
  };

  static defaultProps = {
    ageCheckError: undefined,
    ageCheckMinimumAge: '',
    ageRestrictedProducts: [],
    ageRestrictedProductsCanRemove: false,
    basketUrl: '',
    checkAgeRestrictionDate: window.defaultFunc,
    hasSavedDob: false,
    isApps: false,
    isSignedIn: false,
    rebatchOrderApiCallActive: false,
    removeBasketItemsApiCallActive: false,
    removeBasketItemsFailed: false,
    removeItemsFromBasket: window.defaultFunc,
    showModal: false,
  };

  onFormSubmit = (values) => {
    const { checkAgeRestrictionDate } = this.props;
    const { dateOfBirth } = values;
    return checkAgeRestrictionDate(dateOfBirth, true);
  };

  onRemoveItemsButtonClick = () => {
    const {
      ageRestrictedProducts,
      removeItemsFromBasket,
    } = this.props;

    const items = ageRestrictedProducts.map(item => (item.id));

    removeItemsFromBasket(items);
  };

  render() {
    const {
      showModal,
      isSignedIn,
      hasSavedDob,
      isApps,
      basketUrl,
      ageCheckError,
      ageRestrictedProducts,
      ageCheckMinimumAge,
      ageRestrictedProductsCanRemove,
      removeBasketItemsApiCallActive,
      removeBasketItemsFailed,
      rebatchOrderApiCallActive,
    } = this.props;

    if (!showModal) {
      return null;
    }

    const isSignedInWithDob = isSignedIn && hasSavedDob;

    const formConfig = ageCheckFormConfig(isSignedIn, {
      submitButtonExtra: {
        extra: true,
        name: 'age-check-return-to-basket-link',
        component: Link,
        props: {
          to: basketUrl,
          className: styles.submitButtonExtralink,
          'data-action': 'handback',
          'data-test': 'age-check-modal-return-to-basket',
          children: 'Return to basket',
        },
      },
    });

    const heading = isSignedInWithDob && ageCheckError ?
      `You must be over ${ageCheckMinimumAge} to continue` : 'Age verification';

    const singleItem = ageRestrictedProducts.length === 1;
    const ageRestrictionWithSavedDobBody = singleItem ?
      'Based on the information we have for you, you are not old enough to purchase the following item.' :
      'Based on the information we have for you, you are not old enough to purchase the following items.';
    const ageRestrictionBody = singleItem ? 'The item below is age-restricted. Please confirm your age to continue.' :
      'The items below are age-restricted. Please confirm your age to continue.';
    const body = isSignedInWithDob ? ageRestrictionWithSavedDobBody : ageRestrictionBody;

    return (
      <Modal
        forceFullHeight
        header={heading}
        hideCloseButton
        isOpen
        rootId={appConstants.ROOT}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
      >
        <div
          className={styles.content}
          data-test="age-check-modal"
        >
          <p className={styles.body}>{body}</p>

          {
            ageRestrictedProducts.length &&
            <div className={styles.products}>
              {
                ageRestrictedProducts.map((product, i) => {
                  const key = `product-${i}`;
                  return (
                    <div
                      key={key}
                      className={styles.product}
                      data-test="age-check-modal-product"
                    >
                      <div>
                        <img
                          alt={product.name}
                          src={thumbnail(product.imageUrl)}
                        />
                      </div>
                      <p>{product.name}</p>
                    </div>
                  );
                })
              }
            </div>
          }

          {
            ageCheckError &&
            <div data-test="age-check-modal-errors">

              {
                !isSignedInWithDob &&
                !removeBasketItemsFailed &&
                <Message
                  body={ageCheckError.body}
                  data-test="age-check-error"
                  title={ageCheckError.message}
                  type="error"
                />
              }

              {
                removeBasketItemsFailed &&
                <Message
                  data-test="age-check-remove-failed"
                  title={`Sorry, we couldn't remove the item${singleItem ? '' : 's'}. Please return to basket.`}
                  type="error"
                />
              }

              <div
                className={styles.buttonWrap}
                data-test="age-check-modal-errors-button-wrap"
              >
                {
                  ageRestrictedProductsCanRemove && !removeBasketItemsFailed &&
                  <SecondaryLink
                    data-action="handback"
                    data-test="age-check-modal-return-to-basket"
                    disabled={removeBasketItemsApiCallActive || rebatchOrderApiCallActive}
                    to={basketUrl}
                  >
                    Return to basket
                  </SecondaryLink>
                }
                {
                  (!ageRestrictedProductsCanRemove || (ageRestrictedProductsCanRemove && removeBasketItemsFailed)) &&
                  <PrimaryLink
                    data-action="handback"
                    data-test="age-check-modal-return-to-basket"
                    disabled={removeBasketItemsApiCallActive || rebatchOrderApiCallActive}
                    to={basketUrl}
                  >
                    Return to basket
                  </PrimaryLink>
                }
                {
                  ageRestrictedProductsCanRemove &&
                  !removeBasketItemsFailed &&
                  <PrimaryButton
                    data-test="age-check-modal-remove-items"
                    onClick={this.onRemoveItemsButtonClick}
                    submitting={removeBasketItemsApiCallActive || rebatchOrderApiCallActive}
                  >
                    {singleItem ? 'Remove item and continue' : 'Remove items and continue'}
                  </PrimaryButton>
                }
              </div>

              {
                isSignedInWithDob &&
                <p className={styles.footer}>
                  {
                    isApps &&
                    <>If you feel the date of birth we have for you is incorrect, you can review this in My Account.</>
                  }
                  {
                    !isApps &&
                    <>
                      If you feel the date of birth we have for you is incorrect,
                      you can review this in <MyAccountLink />.
                    </>
                  }
                </p>
              }

            </div>
          }

          {
            !ageCheckError &&
            <Form
              config={formConfig}
              data-test="age-check-modal-form"
              form={formConfig.id}
              multiFormValidation
              onSubmit={this.onFormSubmit}
            />
          }

        </div>

      </Modal>
    );
  }
}

export default connect(state => ({
  showModal: state.user.showAgeCheckModal,
  basketUrl: state.bff.basketUrl,
  isSignedIn: state.user.isSignedIn,
  hasSavedDob: !!state.user.userDob,
  ageCheckError: state.user.ageCheckError,
  ageRestrictedProducts: state.user.ageRestrictedProducts,
  ageRestrictedProductsCanRemove: state.user.ageRestrictedProductsCanRemove,
  ageCheckMinimumAge: state.user.ageCheckMinimumAge,
  isApps: isApps(state),
  removeBasketItemsApiCallActive: state.orderForm.removeBasketItemsApiCallActive,
  removeBasketItemsFailed: state.orderForm.removeBasketItemsFailed,
  rebatchOrderApiCallActive: state.orderForm.rebatchOrderApiCallActive,
}), {
  checkAgeRestrictionDate,
  removeItemsFromBasket,
})(AgeCheckModal);
